import React, { Component, Fragment } from 'react';
import { compose } from '../../reactApolloCompat';
import { withRouter } from '../../utils/withRouter';
import withStyles from '@mui/styles/withStyles';
import gql from 'graphql-tag';
import AttentionDot from '../../components/table/AttentionDot';
import Grid from '../../components/layout/Grid';
import Paper from '../../components/layout/Paper';
import DataTable from '../../components/dataTable/DataTable';
import ViewHeader from '../../components/header/ViewHeader';
import Button from '../../components/form/Button';
import { joinDefined, PAGE_CREMATIONS, prettyPhone } from '../../utils/strings';
import { isNullOrUndefined } from '../../utils/objects';
import { getDeliveryDateTime, getInsertionDateTime, niceTimeFromString, shortDateFromString } from '../../utils/date';
import { DispatchMethodOptions } from '../../components/ComponentConstants';
import ErrorFallback from '../../components/ErrorFallback';
import { withErrorBoundary } from '@sentry/react';

class CremationRecordList extends Component {
    render() {
        return (
            <Grid container className="sticky-table">
                <Grid item>{this.renderHeader()}</Grid>
                <Grid item>
                    <Paper elevation={0}>
                        <Grid item>{this.renderCremationListingsTable()}</Grid>
                    </Paper>
                </Grid>
            </Grid>
        );
    }

    renderHeader() {
        return <ViewHeader title="Cremations" />;
    }

    renderCremationListingsTable() {
        return (
            <Fragment>
                <Grid item>
                    <Grid container>
                        <Grid item>
                            <DataTable
                                dataObject="CremationOrder"
                                queryName="readCremationOrders"
                                fragment={cremationFragment}
                                isFilterable={['Status', 'Dispatch', 'Invoice']}
                                isSortable
                                columns={cremationColumns}
                                variables={{ filterBy: [] }}
                                searchVariable="contains"
                                noFiltersTitle="Only Current Orders"
                                showFlags
                                persistVariablesKey="Cremations"
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Fragment>
        );
    }
}

export const cremationColumns = [
    {
        label: 'Ref',
        propertyPath: 'LegacyKey',
        renderCell: (row, value) =>
            <Button href={PAGE_CREMATIONS + value + `/${row.ID}`} variant="link-orange">
                {value || row.ID}
            </Button>
    },
    {
        label: 'Deceased',
        propertyPath: 'Deceased.Surname',
        renderCell: row => <>{row.NameOfDeceased}</>
    },
    {
        label: <>CR&nbsp;Number</>,
        propertyPath: 'CremationNumber',
        renderCell: row => <>{row.CremationNumber || ''}</>,
        tiny: true
    },
    {
        label: 'Status',
        propertyPath: 'Status',
        renderCell: (row, value) => {
            const approvedAllRequiredDocs = !row.HasMissingCertificates;
            const urgent = !row.ReadyForCremation && !!row.DeliveryDate;
            const ignore = !!row.CremationCancelled || !!row.CremationComplete;
            return (
                <>
                    <div className="attention-list">
                        {(!ignore && (!approvedAllRequiredDocs || !!urgent) && <AttentionDot variant="urgent" />)}
                        {(!!row.SameDayCremation && <AttentionDot variant="sameday" />)}
                        {(!!row.NextDayCremation && <AttentionDot variant="nextday" />)}
                        {('Required' === row.WitnessOfInsertionRequired) && <AttentionDot variant="woi" />}
                        {('Required' === row.ReflectionRoomRequired) && <AttentionDot variant="viewing" />}
                    </div>
                    {value}
                </>
            );
        }
    },
    {
        label: 'Delivery',
        propertyPath: 'CremationDate',
        renderCell: row => <>
            {shortDateFromString(getDeliveryDateTime(row))}
            <br />
            {!!row.DeliveryDate && <small>DELIVERED </small>}
            {niceTimeFromString(getDeliveryDateTime(row))}
        </>
    },
    {
        label: 'Authorised Contact',
        renderCell: row => {
            const funeralDirector = row.Business;
            const firstAuthContact = row.AuthorisedContacts[0];
            if (isNullOrUndefined(firstAuthContact) && funeralDirector.ID < 1) {
                return null;
            }
            if (isNullOrUndefined(firstAuthContact) && funeralDirector.ID > 0) {
                return (
                    <>{funeralDirector.Name}<br />{prettyPhone(funeralDirector.Phone, false)}</>
                );
            }
            return (
                <>
                    {joinDefined(
                        [firstAuthContact.Surname, firstAuthContact.FirstName, firstAuthContact.MiddleName],
                        ' '
                    )}
                    {(firstAuthContact.PrimaryNumber || firstAuthContact.SecondaryNumber) &&
                        (firstAuthContact.FirstName || firstAuthContact.MiddleName || firstAuthContact.Surname) &&
                        <br />}
                    {prettyPhone(
                        joinDefined([firstAuthContact.PrimaryNumber, firstAuthContact.SecondaryNumber], ' / '),
                        false
                    )}
                </>
            );
        }
    },
    {
        label: 'Applicant',
        propertyPath: 'Client.Surname',
        renderCell: row => (
            <>
                {row.Client && joinDefined([row.Client.FirstName, row.Client.MiddleName, row.Client.Surname], ' ')}
                {row.Client &&
                    (row.Client.PrimaryNumber || row.Client.SecondaryNumber) &&
                    (row.Client.FirstName || row.Client.MiddleName || row.Client.Surname) &&
                    <br />}
                {row.Client && prettyPhone(joinDefined([row.Client.PrimaryNumber, row.Client.SecondaryNumber], ' / '), false)}
            </>
        )
    },
    {
        label: 'Insertion',
        propertyPath: 'InsertionDate',
        renderCell: row => <>
            {shortDateFromString(getInsertionDateTime(row))}
            <br />
            {!!row.InsertionDate && <small>INSERTED </small>}
            {niceTimeFromString(getInsertionDateTime(row))}
        </>
    },
    {
        label: 'Dispatches',
        renderCell: row => {
            if (!(row.DispatchDates && row.DispatchDates.length)) return row.CremationComplete ? 'NONE' : null;
            const pendingDates = [].concat(row.DispatchDates.filter(d => !d.DispatchComplete));
            if (!pendingDates.length) return 'Done';
            const dispatchDate = pendingDates[0];
            const dispatchMethod = DispatchMethodOptions.find(e => e.value === dispatchDate.DispatchMethod);
            return (
                <>
                    {((dispatchMethod && dispatchMethod.label) || '?').toUpperCase()}
                    <br />{shortDateFromString(dispatchDate.Date)}
                </>
            );
        }
    },
    {
        label: 'Invoices',
        renderCell: row => {
            if (row.Invoices.filter(obj => ['AUTHORISED', 'SUBMITTED'].includes(obj.XeroStatus)).length > 0)
                return 'UNPAID';
            if (row.Invoices.filter(obj => ['PAID'].includes(obj.XeroStatus)).length > 0
                && row.Invoices.every(obj => ['VOID', 'NOT EXPORTED', 'DELETED', 'PAID'].includes(obj.XeroStatus)))
                return 'Paid';
            return row.CremationComplete ? 'NONE' : '';
        }
    }
];

export const cremationFragment = gql`
    fragment CremationOrderRecord on CremationOrder {
        ID
        LegacyKey
        Status
        CremationCancelled
        CremationNumber
        CremationDate
        CremationTime
        EstimatedDeliveryDate
        EstimatedDeliveryTime
        DeliveryDate
        DeliveryTime
        EstimatedInsertionDate
        EstimatedInsertionTime
        InsertionDate
        InsertionTime
        CremationComplete
        HasMissingCertificates
        NameOfDeceased
        ReflectionRoomRequired
        SameDayCremation
        NextDayCremation
        WitnessOfInsertionRequired
        Invoices {
            ID
            XeroStatus
        }
        DispatchDates {
            ID
            Date
            Time
            DispatchMethod
            AshesCollectedBy
            DispatchComplete
        }
        Client {
            ID
            FirstName
            MiddleName
            Surname
            PrimaryNumber
            SecondaryNumber
        }
        Business {
            ID
            Name
            Phone
        }
        AuthorisedContacts {
            ID
            FirstName
            MiddleName
            Surname
            PrimaryNumber
            SecondaryNumber
        }
    }
`;

export default withErrorBoundary(compose(withRouter, withStyles({}))(CremationRecordList), { fallback: ErrorFallback });
export const CremationRecordAllList = withStyles({})(({ ...props }) => {
    return <CremationRecordList {...props} />;
});
